/**
 * PageSkeleton component
 * 
 * Constitution Section 19.5: React 19 Usage Expectations
 * Suspense fallback for route-level code splitting.
 * 
 * Provides a meaningful loading state while lazy-loaded routes are being fetched.
 */

export function PageSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header skeleton */}
        <div className="mb-8">
          <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse mb-4" />
          <div className="h-4 w-96 bg-slate-200 rounded-lg animate-pulse" />
        </div>

        {/* Content skeleton */}
        <div className="space-y-6">
          {/* Card skeletons */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
            >
              <div className="space-y-4">
                <div className="h-6 w-3/4 bg-slate-200 rounded animate-pulse" />
                <div className="h-4 w-full bg-slate-200 rounded animate-pulse" />
                <div className="h-4 w-5/6 bg-slate-200 rounded animate-pulse" />
                <div className="flex gap-4 mt-4">
                  <div className="h-10 w-24 bg-slate-200 rounded-lg animate-pulse" />
                  <div className="h-10 w-24 bg-slate-200 rounded-lg animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact skeleton for smaller pages
 */
export function CompactPageSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
        <div className="space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded-lg animate-pulse mx-auto" />
          <div className="h-4 w-full bg-slate-200 rounded animate-pulse" />
          <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse mx-auto" />
          <div className="h-10 w-full bg-slate-200 rounded-lg animate-pulse mt-6" />
        </div>
      </div>
    </div>
  );
}

/**
 * Chat page skeleton (specific for chat interface)
 */
export function ChatPageSkeleton() {
  return (
    <div className="h-screen bg-slate-50 flex">
      {/* Sidebar skeleton */}
      <div className="w-80 border-r border-slate-200 bg-white p-6 space-y-4">
        <div className="h-8 w-32 bg-slate-200 rounded-lg animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>

      {/* Main content skeleton */}
      <div className="flex-1 flex flex-col">
        <div className="h-16 border-b border-slate-200 bg-white px-6 flex items-center">
          <div className="h-6 w-48 bg-slate-200 rounded-lg animate-pulse" />
        </div>
        <div className="flex-1 p-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-white rounded-lg border border-slate-200 animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}
